export function progressPercent(current, target) {
  if (!Number.isFinite(current) || !Number.isFinite(target) || target <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((current / target) * 100)));
}

export function remainingAmount(current, target) {
  if (!Number.isFinite(current) || !Number.isFinite(target)) return 0;
  return Math.max(0, target - current);
}

export function objectiveProgress(start, current, target) {
  if (![start, current, target].every(Number.isFinite)) return 0;
  const total = target - start;
  if (total === 0) return 100;
  const travelled = current - start;
  return Math.min(100, Math.max(0, Math.round((travelled / total) * 100)));
}

export function chartPoints(values, width, height, dates = [], options = {}) {
  if (!values.length) return "";
  const min = options.min ?? Math.min(...values) - 0.3;
  const max = options.max ?? Math.max(...values) + 0.3;
  const left = options.left ?? 20;
  const right = options.right ?? 20;
  const top = options.top ?? 20;
  const bottom = options.bottom ?? 15;
  const timestamps = dates.map((date) => Date.parse(`${date}T12:00:00Z`));
  const chronological = timestamps.length === values.length && timestamps.every(Number.isFinite);
  const requestedStart = options.startDate ? Date.parse(`${options.startDate}T12:00:00Z`) : NaN;
  const requestedEnd = options.endDate ? Date.parse(`${options.endDate}T12:00:00Z`) : NaN;
  const firstTimestamp = chronological ? (Number.isFinite(requestedStart) ? requestedStart : Math.min(...timestamps)) : 0;
  const lastTimestamp = chronological ? (Number.isFinite(requestedEnd) ? requestedEnd : Math.max(...timestamps)) : 0;
  const dateRange = chronological ? Math.max(0, lastTimestamp - firstTimestamp) : 0;
  return values.map((value, index) => {
    const progress = dateRange > 0
      ? (timestamps[index] - firstTimestamp) / dateRange
      : values.length === 1 ? 0.5 : index / Math.max(1, values.length - 1);
    const x = left + progress * (width - left - right);
    const y = top + ((max - value) / Math.max(0.1, max - min)) * (height - top - bottom);
    return `${x},${y}`;
  }).join(" ");
}

export function nutritionTotals(entries) {
  return entries.filter((entry) => entry.included).reduce((sum, entry) => ({
    calories: sum.calories + Number(entry.calories || 0),
    protein: sum.protein + Number(entry.protein || 0),
    carbs: sum.carbs + Number(entry.carbs || 0),
    fat: sum.fat + Number(entry.fat || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
}

export function weeklyReport(entries, sessions, measurements, anchorDate, calorieTarget, dailyActivity = [], previousReport = null) {
  const anchor = new Date(`${anchorDate}T12:00:00Z`);
  const monday = new Date(anchor);
  monday.setUTCDate(anchor.getUTCDate() - ((anchor.getUTCDay() + 6) % 7));
  const from = monday.toISOString().slice(0, 10);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const to = sunday.toISOString().slice(0, 10);
  const weekEntries = entries.filter((item) => item.included && item.date >= from && item.date <= to);
  const trackedDays = new Set(weekEntries.map((item) => item.date)).size;
  const completed = sessions.filter((item) => item.status === "completed" && item.date >= from && item.date <= to);
  const planned = sessions.filter((item) => item.status === "planned" && item.date >= from && item.date <= to);
  const skipped = sessions.filter((item) => item.status === "skipped" && item.date >= from && item.date <= to);
  const overdue = sessions.filter((item) => item.status === "overdue" && item.date >= from && item.date <= to);
  const volumeKg = completed.flatMap((item) => item.steps).reduce((total, step) => total + Number(step.actualSets || 0) * Number(step.actualReps || 0) * Number(step.actualLoadKg || 0), 0);
  const macroAverage = (key) => trackedDays ? Math.round((weekEntries.reduce((sum, item) => sum + Number(item[key] || 0), 0) / trackedDays) * 10) / 10 : null;
  const activity = dailyActivity.filter((item) => item.date >= from && item.date <= to);
  const totalSteps = activity.reduce((sum, item) => sum + Number(item.steps || 0), 0);
  return { from, to, averageCalories: trackedDays ? Math.round(weekEntries.reduce((sum, item) => sum + Number(item.calories || 0), 0) / trackedDays) : 0, trackedDays, targetCalories: calorieTarget, completedSessions: completed.length, plannedSessions: planned.length, skippedSessions: skipped.length, overdueSessions: overdue.length, trainingMinutes: completed.reduce((sum, item) => sum + Number(item.actualDurationMinutes || item.durationMinutes || 0), 0), distanceMeters: completed.reduce((sum, item) => sum + Number(item.actualDistanceMeters || 0), 0), volumeKg: Math.round(volumeKg * 10) / 10, averageProtein: macroAverage("protein"), averageCarbs: macroAverage("carbs"), averageFat: macroAverage("fat"), totalSteps, averageSteps: activity.length ? Math.round(totalSteps / activity.length) : 0, stepDays: activity.length, measurements: measurements.filter((item) => item.date >= from && item.date <= to), previous: previousReport ? { averageCalories: previousReport.averageCalories, completedSessions: previousReport.completedSessions, trainingMinutes: previousReport.trainingMinutes, distanceMeters: previousReport.distanceMeters, volumeKg: previousReport.volumeKg, totalSteps: previousReport.totalSteps } : undefined };
}

export function trainingWeek(sessions, anchorDate) {
  const anchor = new Date(`${anchorDate}T12:00:00Z`);
  const monday = new Date(anchor);
  monday.setUTCDate(anchor.getUTCDate() - ((anchor.getUTCDay() + 6) % 7));
  const dates = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setUTCDate(monday.getUTCDate() + index);
    return date.toISOString().slice(0, 10);
  });
  const normalized = sessions.map((session) => session.status === "planned" && session.date < anchorDate ? { ...session, status: "overdue" } : session);
  const weekSessions = normalized.filter((session) => session.date >= dates[0] && session.date <= dates[6]);
  const completed = weekSessions.filter((session) => session.status === "completed");
  const planned = weekSessions.filter((session) => session.status === "planned");
  const skipped = weekSessions.filter((session) => session.status === "skipped");
  const days = dates.map((date) => {
    const daySessions = weekSessions.filter((session) => session.date === date);
    return daySessions.some((session) => session.status === "completed") ? "done" : daySessions.some((session) => session.status === "overdue") ? "overdue" : daySessions.some((session) => session.status === "planned") ? "planned" : daySessions.some((session)=>session.status==="skipped") ? "skipped" : "";
  });
  return {
    from: dates[0],
    to: dates[6],
    sessions: weekSessions,
    completed: completed.length,
    planned: planned.length,
    skipped: skipped.length,
    total: weekSessions.length,
    progress: weekSessions.length ? Math.round((completed.length / weekSessions.length) * 100) : 0,
    days,
  };
}

export function monthCalendar(sessions, anchorDate) {
  const anchor = new Date(`${anchorDate}T12:00:00Z`);
  const first = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1, 12));
  const start = new Date(first);
  start.setUTCDate(first.getUTCDate() - ((first.getUTCDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setUTCDate(start.getUTCDate() + index);
    const iso = date.toISOString().slice(0, 10);
    return { date: iso, day: date.getUTCDate(), inMonth: date.getUTCMonth() === anchor.getUTCMonth(), sessions: sessions.filter((session) => session.date === iso) };
  });
}

export function personalRecords(sessions) {
  const records = new Map();
  const setRecord = (key, candidate) => { const current = records.get(key); if (!current || candidate.value > current.value) records.set(key, candidate); };
  for (const session of sessions.filter((item) => item.status === "completed")) {
    if (session.actualDistanceMeters) setRecord(`${session.sport}:distance`, { id: `${session.sport}:distance`, sportId: session.sport, metric: "distance", label: "Plus longue distance", value: session.actualDistanceMeters, unit: "m", sessionId: session.id, achievedOn: session.date });
    for (const step of session.steps || []) {
      if (step.actualLoadKg) setRecord(`${session.sport}:${step.exerciseId || step.label}:load`, { id: `${session.sport}:${step.exerciseId || step.label}:load`, sportId: session.sport, exerciseId: step.exerciseId, metric: "load", label: `Charge maximale · ${step.label}`, value: step.actualLoadKg, unit: "kg", sessionId: session.id, achievedOn: session.date });
      const volume = Number(step.actualSets || 0) * Number(step.actualReps || 0) * Number(step.actualLoadKg || 0);
      if (volume) setRecord(`${session.sport}:${step.exerciseId || step.label}:volume`, { id: `${session.sport}:${step.exerciseId || step.label}:volume`, sportId: session.sport, exerciseId: step.exerciseId, metric: "volume", label: `Meilleur volume · ${step.label}`, value: volume, unit: "kg", sessionId: session.id, achievedOn: session.date });
      if (step.actualLoadKg && step.actualReps) { const estimated = step.actualLoadKg * (1 + step.actualReps / 30); setRecord(`${session.sport}:${step.exerciseId || step.label}:1rm`, { id: `${session.sport}:${step.exerciseId || step.label}:1rm`, sportId: session.sport, exerciseId: step.exerciseId, metric: "estimated_1rm", label: `1RM estimé · ${step.label}`, value: Math.round(estimated * 10) / 10, unit: "kg", sessionId: session.id, achievedOn: session.date, estimated: true }); }
    }
  }
  return [...records.values()];
}
